import { useState, useEffect } from 'react';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '@municipality/firebase';
import { Timestamp } from 'firebase/firestore';

// Event types (matching municipality app structure)
export enum EventStatus {
  DRAFT = 'draft',
  PUBLISHED = 'published',
  CANCELLED = 'cancelled',
  COMPLETED = 'completed'
}

export enum EventCategory {
  SEMINAR = 'seminar',
  FESTIVAL = 'festival',
  WORKSHOP = 'workshop',
  SPORTS = 'sports',
  MUSIC = 'music',
  CONFERENCE = 'conference',
  EXHIBITION = 'exhibition',
  THEATER = 'theater',
  CULTURAL = 'cultural',
  EDUCATIONAL = 'educational',
  BUSINESS = 'business',
  HEALTH = 'health',
  ENVIRONMENT = 'environment',
  TECHNOLOGY = 'technology',
  COMMUNITY = 'community',
  CHARITY = 'charity',
  POLITICAL = 'political',
  RELIGIOUS = 'religious',
  ENTERTAINMENT = 'entertainment',
  OTHER = 'other'
}

export interface EventLocation {
  venueName: string;
  address: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
}

export interface EventTranslation {
  title: string;
  description: string;
  summary?: string;
}

export interface Event {
  id: string;
  municipalityId: string;
  translations: Record<string, EventTranslation>;
  category: EventCategory;
  status: EventStatus;
  location: EventLocation;
  startDate: Date;
  endDate: Date;
  isFullDay: boolean;
  startDateDisplay?: string;
  endDateDisplay?: string;
  featuredImage?: string | null;
  organizer?: {
    name: string;
    email: string;
    phone?: string;
    website?: string;
  };
  sponsors: Array<{
    name: string;
    phone?: string;
    website?: string;
  }>;
  tags: string[];
  externalLink?: string;
  authorId: string;
  authorName: string;
  createdAt: Date;
  updatedAt: Date;
  publishedAt?: Date;
  viewCount: number;
  uniqueViewCount: number;
  registrationCount: number;
  slug: string;
}

interface EventStatistics {
  total: number;
  upcoming: number;
  ongoing: number;
  past: number;
  totalRegistrations: number;
  averageRegistrations: number;
}

interface EventListResponse {
  items: Event[];
  total: number;
  statistics: EventStatistics;
}

// Helper to convert Firestore Timestamp to Date
const toDate = (value: unknown): Date => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (value instanceof Timestamp) return value.toDate();
  if (typeof value === 'string') return new Date(value);
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  return new Date();
};

// Convert Firestore data to Event
const convertFirestoreData = (data: unknown, id: string, municipalityId: string): Event => {
  const docData = data as Record<string, unknown>;
  
  // Convert translations to proper format
  const translationsData = docData.translations as Record<string, { title?: string; description?: string; summary?: string }> | undefined;
  const translations: Record<string, EventTranslation> = {};
  if (translationsData) {
    Object.keys(translationsData).forEach((lang) => {
      const trans = translationsData[lang];
      if (trans) {
        translations[lang] = {
          title: trans.title || '',
          description: trans.description || '',
          summary: trans.summary,
        };
      }
    });
  }

  // Convert location
  const locationData = docData.location as Record<string, unknown> | undefined;
  const location: EventLocation = locationData ? {
    venueName: (locationData.venueName as string) || '',
    address: (locationData.address as string) || '',
    coordinates: locationData.coordinates as { lat: number; lng: number } | undefined,
  } : {
    venueName: '',
    address: '',
  };

  const startDate = toDate(docData.startDate);
  const endDate = toDate(docData.endDate);

  return {
    id,
    municipalityId,
    translations,
    category: (docData.category as EventCategory) || EventCategory.OTHER,
    status: (docData.status as EventStatus) || EventStatus.DRAFT,
    location,
    startDate,
    endDate,
    isFullDay: (docData.isFullDay as boolean) || false,
    startDateDisplay: docData.startDateDisplay as string | undefined,
    endDateDisplay: docData.endDateDisplay as string | undefined,
    featuredImage: typeof docData.featuredImage === 'string' ? docData.featuredImage : 
                  (docData.featuredImage && typeof docData.featuredImage === 'object' && 'url' in docData.featuredImage) 
                    ? (docData.featuredImage as { url?: string }).url || null 
                    : null,
    organizer: docData.organizer as Event['organizer'] | undefined,
    sponsors: (docData.sponsors as Event['sponsors']) || [],
    tags: (docData.tags as string[]) || [],
    externalLink: docData.externalLink as string | undefined,
    authorId: (docData.authorId as string) || '',
    authorName: (docData.authorName as string) || '',
    createdAt: toDate(docData.createdAt),
    updatedAt: toDate(docData.updatedAt),
    publishedAt: docData.publishedAt ? toDate(docData.publishedAt) : undefined,
    viewCount: (docData.viewCount as number) || 0,
    uniqueViewCount: (docData.uniqueViewCount as number) || 0,
    registrationCount: (docData.registrationCount as number) || 0,
    slug: (docData.slug as string) || '',
  };
};

export const useMunicipalityEvents = (municipalityId: string | null) => {
  const [data, setData] = useState<EventListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!municipalityId) {
      setLoading(false);
      setData(null);
      return;
    }

    const fetchEvents = async () => {
      try {
        setLoading(true);
        setError(null);

        const eventsCollection = collection(db, 'municipalities', municipalityId, 'events');
        const snapshot = await getDocs(query(eventsCollection, orderBy('startDate', 'desc')));

        const items: Event[] = [];
        let totalRegistrations = 0;
        let upcomingCount = 0;
        let ongoingCount = 0;
        let pastCount = 0;

        const now = new Date();

        snapshot.forEach((doc) => {
          const eventItem = convertFirestoreData(doc.data(), doc.id, municipalityId);
          items.push(eventItem);

          // Calculate statistics
          totalRegistrations += eventItem.registrationCount || 0;
          
          // Categorize events by date
          if (eventItem.status === EventStatus.CANCELLED) {
            // Cancelled events don't count in upcoming/ongoing/past
            return;
          }
          
          if (eventItem.endDate < now) {
            pastCount++;
          } else if (eventItem.startDate <= now && eventItem.endDate >= now) {
            ongoingCount++;
          } else if (eventItem.startDate > now) {
            upcomingCount++;
          }
        });

        const total = items.length;
        const averageRegistrations = total > 0 ? Math.round(totalRegistrations / total) : 0;

        setData({
          items,
          total,
          statistics: {
            total,
            upcoming: upcomingCount,
            ongoing: ongoingCount,
            past: pastCount,
            totalRegistrations,
            averageRegistrations,
          },
        });
      } catch (err) {
        console.error('Error fetching municipality events:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch municipality events');
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [municipalityId]);

  return { data, loading, error };
};
